import {
    IpgSystemId,
    IpgSystemIdKey,
    IpgSystemIdKeyList,
    IpwSystemId,
    IpwSystemIdKey,
    IpwSystemIdKeyList,
    ThirdPartySystemId,
} from './types/third-party-system.enums';
import { ThirdPartySystemIdSchema as ThirdPartySystemIdValueSchema } from './schemas/third-party-system.schemas';

import { z } from 'zod';

export const RpaModuleName = {
    PCPARTPICKER_IPW: 'pcpartpicker-ipw' as const,
    DEALERTRACK_IPW: 'dealertrack-ipw' as const,
    VALORPAYTECH_IPG: 'valorpaytech-ipg' as const,
    PCPARTPICKER_WORKFLOWS: 'pcpartpicker-workflows' as const,
    VALORPAYTECH_WORKFLOWS: 'valorpaytech-workflows' as const,
    STONEPROFITS_WORKFLOWS: 'stoneprofits-workflows' as const,
    GENERIC_WEBSITE_WORKFLOWS: 'generic-website-workflows' as const,
} as const;
export type RpaModuleName = (typeof RpaModuleName)[keyof typeof RpaModuleName];
export const ALL_RPA_MODULE_NAMES: RpaModuleName[] = Object.values(RpaModuleName);
const RpaModuleNameValueSet: ReadonlySet<string> = new Set(ALL_RPA_MODULE_NAMES);
export const RpaModuleNameSchema: z.ZodType<RpaModuleName> = z.enum(
    ALL_RPA_MODULE_NAMES
);

export function isRpaModuleName(moduleName: string): moduleName is RpaModuleName {
    return RpaModuleNameValueSet.has(moduleName);
}

export const SessionPersistenceType = {
    EPHEMERAL: 'ephemeral' as const,
    PERSISTENT: 'persistent' as const,
} as const;
export type SessionPersistenceType =
    (typeof SessionPersistenceType)[keyof typeof SessionPersistenceType];

export const IpWebsiteType = {
    PORTAL: 'portal' as const,
    IPG: 'ipg' as const,
    IPW: 'ipw' as const,
    UNMANAGED: 'unmanaged' as const,
} as const;
export type IpWebsiteType = (typeof IpWebsiteType)[keyof typeof IpWebsiteType];

export const MerchantWebsiteType = {
    IPG: IpWebsiteType.IPG,
    IPW: IpWebsiteType.IPW,
} as const;
export type MerchantWebsiteType = (typeof MerchantWebsiteType)[keyof typeof MerchantWebsiteType];
// Zod schema for SessionPersistenceType
export const SessionPersistenceTypeSchema = z.union([
    z.literal('ephemeral'),
    z.literal('persistent'),
]);

export const UrlPatternsRuleAction = {
    ALLOW: 'allow' as const,
    DENY: 'deny' as const,
} as const;
export type UrlPatternsRuleAction =
    (typeof UrlPatternsRuleAction)[keyof typeof UrlPatternsRuleAction];

export const UrlPatternsRuleActionSchema: z.ZodType<UrlPatternsRuleAction> = z.enum([
    UrlPatternsRuleAction.ALLOW,
    UrlPatternsRuleAction.DENY,
]);

export function isValidUrlPatternString(pattern: string): boolean {
    try {
        new URLPattern(pattern);
        return true;
    } catch {
        return false;
    }
}

export type UrlPatternString = string;

export const UrlPatternStringSchema: z.ZodType<UrlPatternString> = z
    .string()
    .min(1)
    .refine(isValidUrlPatternString, {
        message: 'Invalid URLPattern syntax.',
    });

export const UrlPatternStringArraySchema: z.ZodType<UrlPatternString[]> = z
    .array(UrlPatternStringSchema)
    .min(1);

export const UrlPatternStringListSchema: z.ZodType<UrlPatternString[]> =
    UrlPatternStringArraySchema;

export function canonicalizeUrlPatternStrings(
    patterns: readonly string[]
): UrlPatternString[] {
    if (patterns.length === 0) {
        throw new Error('At least one URLPattern string is required.');
    }

    const validPatterns: UrlPatternString[] = [];
    for (const pattern of patterns) {
        if (pattern.trim() !== pattern) {
            throw new Error('URLPattern strings may not include surrounding whitespace.');
        }
        validPatterns.push(UrlPatternStringSchema.parse(pattern));
    }

    return [...new Set(validPatterns)].sort();
}

export type UrlPatternsRule = {
    action: UrlPatternsRuleAction;
    urlPatterns: UrlPatternString[];
};

export const UrlPatternsRuleSchema: z.ZodType<UrlPatternsRule> = z.object({
    action: UrlPatternsRuleActionSchema,
    urlPatterns: UrlPatternStringArraySchema,
});
export type UrlPatternsRules = UrlPatternsRule;
export const UrlPatternsRulesSchema = UrlPatternsRuleSchema;

export interface AllowedWebsiteBase {
    system_id: ThirdPartySystemId;
    template_name?: string;
    name: string;
    url: string;
    allowedUrlPatterns: UrlPatternString[];
    sessionPersistence: SessionPersistenceType;
    promoteSessionCookies: boolean;
    urlPatternsRules: UrlPatternsRule[];
    fetchDefaultAction: UrlPatternsRuleAction;
    iframeUrlPatterns: UrlPatternsRule[];
    iframeDefaultAction: UrlPatternsRuleAction;
}

export type MerchantAllowedWebsite = AllowedWebsiteBase & { type: MerchantWebsiteType };
export type IpwAllowedWebiste = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPW };
export type IpgAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPG };
export type PortalAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.PORTAL };
export type UnmanagedAllowedWebsite = AllowedWebsiteBase & {
    type: typeof IpWebsiteType.UNMANAGED;
};
export type AllowedWebsite =
    | UnmanagedAllowedWebsite
    | MerchantAllowedWebsite
    | PortalAllowedWebsite;

// Zod schema for AllowedWebsite
export const AllowedWebsiteSchemaBase: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdValueSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
}> = z.object({
    system_id: ThirdPartySystemIdValueSchema,
    template_name: z.string().trim().min(1).optional(),
    name: z.string(),
    url: z.string().url(),
    allowedUrlPatterns: UrlPatternStringArraySchema,
    sessionPersistence: SessionPersistenceTypeSchema,
    promoteSessionCookies: z.boolean().default(false),
    urlPatternsRules: z.array(UrlPatternsRuleSchema).default([]),
    fetchDefaultAction: UrlPatternsRuleActionSchema.default(UrlPatternsRuleAction.ALLOW),
    iframeUrlPatterns: z.array(UrlPatternsRuleSchema).default([]),
    iframeDefaultAction: UrlPatternsRuleActionSchema.default(UrlPatternsRuleAction.ALLOW),
});

export const AllowedWebsiteSchemaIPG: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdValueSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodDefault<z.ZodLiteral<'ipg'>>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPG).default(MerchantWebsiteType.IPG),
});

export const AllowedWebsiteSchemaIPW: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdValueSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodDefault<z.ZodLiteral<'ipw'>>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPW).default(MerchantWebsiteType.IPW),
});

export const AllowedWebsiteSchemaUnmanaged: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdValueSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodLiteral<'unmanaged'>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(IpWebsiteType.UNMANAGED),
});

// Zod schema for IpwConfig
export interface IpwConfigBase {
    ipw_id: number;
    ipw_system_id: IpwSystemIdKey;
    name: string;
    urlPattern: UrlPatternString;
    workflow_prefix: string;
    allowedWebsites: IpwAllowedWebiste[];
    rpaModules: RpaModuleName[];
}

export interface IpwConfigDealertrack extends IpwConfigBase {
    ipw_system_id: typeof IpwSystemIdKey.DEALERTRACK;
    reconciliation: {
        paymentCode: string;
        doInjectApprovalNumber: boolean;
        doFinalizeReconciliation: boolean;
    };
}

export interface IpwConfigPcpartpicker extends IpwConfigBase {
    ipw_system_id: typeof IpwSystemIdKey.PCPARTPICKER;
}

export type IpwConfig = IpwConfigPcpartpicker | IpwConfigDealertrack;

export const IpwConfigBaseSchema: z.ZodObject<{
    ipw_id: z.ZodNumber;
    ipw_system_id: z.ZodType<IpwSystemIdKey>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
}> = z.object({
    ipw_id: z.number(),
    ipw_system_id: z.enum(IpwSystemIdKeyList),
    name: z.string(),
    urlPattern: UrlPatternStringSchema,
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPW).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
});

export const IpwConfigDealertrackSchema: z.ZodObject<{
    ipw_id: z.ZodNumber;
    ipw_system_id: z.ZodLiteral<typeof IpwSystemIdKey.DEALERTRACK>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    reconciliation: z.ZodObject<{
        paymentCode: z.ZodString;
        doInjectApprovalNumber: z.ZodBoolean;
        doFinalizeReconciliation: z.ZodBoolean;
    }>;
}> = IpwConfigBaseSchema.extend({
    ipw_system_id: z.literal(IpwSystemIdKey.DEALERTRACK),
    reconciliation: z.object({
        paymentCode: z.string().min(1).max(2),
        doInjectApprovalNumber: z.boolean(),
        doFinalizeReconciliation: z.boolean(),
    }),
});

export const IpwConfigPcpartpickerSchema: z.ZodObject<{
    ipw_id: z.ZodNumber;
    ipw_system_id: z.ZodLiteral<typeof IpwSystemIdKey.PCPARTPICKER>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
}> = IpwConfigBaseSchema.extend({
    ipw_system_id: z.literal(IpwSystemIdKey.PCPARTPICKER),
});

export const IpwConfigSchema: z.ZodDiscriminatedUnion<
    [typeof IpwConfigPcpartpickerSchema, typeof IpwConfigDealertrackSchema],
    'ipw_system_id'
> = z.discriminatedUnion(
    'ipw_system_id',
    [IpwConfigPcpartpickerSchema, IpwConfigDealertrackSchema]
);


// Zod schema for IpgConfig
export interface BaseIpgConfig {
    ipg_id: number;
    name: string;
    urlPattern: UrlPatternString;
    workflow_prefix: string;
    allowedWebsites: IpgAllowedWebsite[];
    rpaModules: RpaModuleName[];
}

export interface ValorIpgConfig extends BaseIpgConfig {
    ipg_id: typeof IpgSystemId.VALOR_US;
    default_api_url?: string;
    default_channel?: string;
}

export type IpgConfig = ValorIpgConfig;

export const BaseIpgConfigSchema: z.ZodObject<{
    ipg_id: z.ZodNumber;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPG>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
}> = z.object({
    ipg_id: z.number(),
    name: z.string(),
    urlPattern: UrlPatternStringSchema,
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPG).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
});

export const ValorIpgConfigSchema: z.ZodObject<{
    ipg_id: z.ZodLiteral<1>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPG>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    default_api_url: z.ZodOptional<z.ZodString>;
    default_channel: z.ZodOptional<z.ZodString>;
}> = BaseIpgConfigSchema.extend({
    ipg_id: z.literal(IpgSystemId.VALOR_US),
    default_api_url: z.string().url().optional(),
    default_channel: z.string().trim().min(1).optional(),
});

export const IpgConfigSchema: z.ZodDiscriminatedUnion<
    [typeof ValorIpgConfigSchema],
    'ipg_id'
> = z.discriminatedUnion('ipg_id', [ValorIpgConfigSchema]);

export interface SambaCredentialDefinitionMetadata {
    ipx_credential_definition_uuid: string;
    system_id: ThirdPartySystemId;
    credential_definition_key: string;
    trigger_url_patterns: UrlPatternString[];
}

export interface SambaCredentialMetadata {
    credential_uuid: string;
    account_id: string;
    definition: SambaCredentialDefinitionMetadata;
}

export const SambaCredentialDefinitionMetadataSchema:
    z.ZodType<SambaCredentialDefinitionMetadata> = z.object({
    ipx_credential_definition_uuid: z.string().uuid(),
    system_id: ThirdPartySystemIdValueSchema,
    credential_definition_key: z.string(),
    trigger_url_patterns: UrlPatternStringArraySchema,
});

export const SambaCredentialMetadataSchema: z.ZodType<SambaCredentialMetadata> =
    z.object({
        credential_uuid: z.string().uuid(),
        account_id: z.string(),
        definition: SambaCredentialDefinitionMetadataSchema,
    });

export interface SambaConfig {
    allowPromoteSessionCookie: boolean;
    unmanagedAllowedWebsites: UnmanagedAllowedWebsite[];
    credentialMetadata: SambaCredentialMetadata[];
}

export interface StoredSambaConfig {
    allowPromoteSessionCookie?: boolean;
    credentialMetadata?: SambaCredentialMetadata[];
}

// Zod schema for SambaConfig
export const SambaConfigSchema: z.ZodType<SambaConfig> = z.object({
    allowPromoteSessionCookie: z.boolean().default(false),
    unmanagedAllowedWebsites: z.array(AllowedWebsiteSchemaUnmanaged).default([]),
    credentialMetadata: z.array(SambaCredentialMetadataSchema).default([]),
});

export const StoredSambaConfigSchema: z.ZodType<StoredSambaConfig> =
    z.object({
        allowPromoteSessionCookie: z.boolean().optional(),
        credentialMetadata: z.array(SambaCredentialMetadataSchema).optional(),
    });

export const StoredSambaConfigWriteSchema: z.ZodType<StoredSambaConfig> =
    z.object({
        allowPromoteSessionCookie: z.boolean().optional(),
        credentialMetadata: z.array(SambaCredentialMetadataSchema).optional(),
    }).strict();

export const CustomerPortalDeliveryChannel = {
    EMAIL: 'EMAIL' as const,
    SMS: 'SMS' as const,
} as const;
export type CustomerPortalDeliveryChannel =
    (typeof CustomerPortalDeliveryChannel)[keyof typeof CustomerPortalDeliveryChannel];

export const CustomerPortalDeliveryChannelSchema = z.enum(CustomerPortalDeliveryChannel);

export const E164PhoneNumberSchema = z.string().regex(/^\+[1-9]\d{1,14}$/);
export type E164PhoneNumber = z.infer<typeof E164PhoneNumberSchema>;

export const PaymentRequestDocumentPolicySchema = z.object({
    allowUploadedDocuments: z.boolean().default(true),
    allowGeneratedDocuments: z.boolean().default(true),
    requireDocument: z.boolean().default(false),
});
export type PaymentRequestDocumentPolicy = z.infer<typeof PaymentRequestDocumentPolicySchema>;

export const PaymentRequestTemplateSelectionSchema = z.object({
    paymentRequestDetailsTypeId: z.string().min(1),
    paymentRequestDetailsTypeVersion: z.number().int().nonnegative(),
    documentTemplateUuid: z.string().uuid(),
});
export type PaymentRequestTemplateSelection = z.infer<
    typeof PaymentRequestTemplateSelectionSchema
>;

export interface CustomerPortalTwilioSmsDeliveryConfigEnabled {
    readonly enabled: true;
    readonly fromPhoneNumber?: E164PhoneNumber | undefined;
    readonly messagingServiceSid?: string | undefined;
}

export interface CustomerPortalTwilioSmsDeliveryConfigDisabled {
    readonly enabled: false;
    readonly fromPhoneNumber?: E164PhoneNumber | undefined;
    readonly messagingServiceSid?: string | undefined;
}

export type CustomerPortalTwilioSmsDeliveryConfig =
    | CustomerPortalTwilioSmsDeliveryConfigEnabled
    | CustomerPortalTwilioSmsDeliveryConfigDisabled;

export const CustomerPortalTwilioSmsDeliveryConfigEnabledSchema: z.ZodObject<{
    enabled: z.ZodLiteral<true>;
    fromPhoneNumber: z.ZodOptional<typeof E164PhoneNumberSchema>;
    messagingServiceSid: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        enabled: z.literal(true),
        fromPhoneNumber: E164PhoneNumberSchema.optional(),
        messagingServiceSid: z.string().min(1).optional(),
    })
    .refine(
        (value) =>
            (value.fromPhoneNumber !== undefined) !==
            (value.messagingServiceSid !== undefined),
        {
            message:
                'Enabled Twilio SMS config requires exactly one sender field.',
        }
    );

export const CustomerPortalTwilioSmsDeliveryConfigDisabledSchema: z.ZodObject<{
    enabled: z.ZodLiteral<false>;
    fromPhoneNumber: z.ZodOptional<typeof E164PhoneNumberSchema>;
    messagingServiceSid: z.ZodOptional<z.ZodString>;
}> = z.object({
    enabled: z.literal(false),
    fromPhoneNumber: E164PhoneNumberSchema.optional(),
    messagingServiceSid: z.string().min(1).optional(),
});

export const CustomerPortalTwilioSmsDeliveryConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof CustomerPortalTwilioSmsDeliveryConfigEnabledSchema,
        typeof CustomerPortalTwilioSmsDeliveryConfigDisabledSchema,
    ],
    'enabled'
> = z.discriminatedUnion('enabled', [
    CustomerPortalTwilioSmsDeliveryConfigEnabledSchema,
    CustomerPortalTwilioSmsDeliveryConfigDisabledSchema,
]);

export const CustomerPortalSendgridEmailDeliveryConfigSchema = z.object({
    enabled: z.boolean().default(false),
    fromEmail: z.string().email(),
    fromName: z.string().min(1).optional(),
    replyToEmail: z.string().email().optional(),
});
export type CustomerPortalSendgridEmailDeliveryConfig = z.infer<
    typeof CustomerPortalSendgridEmailDeliveryConfigSchema
>;

export const CustomerPortalPaymentRequestsConfigSchema = z.object({
    enabled: z.boolean().default(false),
    allowedDeliveryChannels: z.array(CustomerPortalDeliveryChannelSchema).default([]),
    documentPolicy: PaymentRequestDocumentPolicySchema.optional(),
    templateSelections: z.array(PaymentRequestTemplateSelectionSchema).default([]),
    twilioSms: CustomerPortalTwilioSmsDeliveryConfigSchema.optional(),
    sendgridEmail: CustomerPortalSendgridEmailDeliveryConfigSchema.optional(),
});
export type CustomerPortalPaymentRequestsConfig = z.infer<
    typeof CustomerPortalPaymentRequestsConfigSchema
>;

export const CustomerPortalConfigSchema = z.object({
    paymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
});
export type CustomerPortalConfig = z.infer<typeof CustomerPortalConfigSchema>;

export const IpgSystemIdSchema = z.enum(IpgSystemIdKeyList);
export const IpwSystemIdSchema = z.enum(IpwSystemIdKeyList);

export type AllowedWebsiteCustomization = Pick<
    AllowedWebsiteBase,
    | 'allowedUrlPatterns'
    | 'sessionPersistence'
    | 'promoteSessionCookies'
    | 'urlPatternsRules'
    | 'fetchDefaultAction'
    | 'iframeUrlPatterns'
    | 'iframeDefaultAction'
>;

export const AllowedWebsiteCustomizationSchema: z.ZodType<AllowedWebsiteCustomization> =
    z
        .object({
            allowedUrlPatterns: UrlPatternStringArraySchema,
            sessionPersistence: SessionPersistenceTypeSchema,
            promoteSessionCookies: z.boolean().default(false),
            urlPatternsRules: z.array(UrlPatternsRuleSchema).default([]),
            fetchDefaultAction: UrlPatternsRuleActionSchema.default(
                UrlPatternsRuleAction.ALLOW
            ),
            iframeUrlPatterns: z.array(UrlPatternsRuleSchema).default([]),
            iframeDefaultAction: UrlPatternsRuleActionSchema.default(
                UrlPatternsRuleAction.ALLOW
            ),
        })
        .strict();

export type DealertrackReconciliationCustomization =
    IpwConfigDealertrack['reconciliation'];

export const DealertrackReconciliationCustomizationSchema: z.ZodType<DealertrackReconciliationCustomization> =
    IpwConfigDealertrackSchema.shape.reconciliation;

export type IpwConfigBaseCustomization = {
    ipw_system_id: IpwConfigBase['ipw_system_id'];
    urlPattern: IpwConfigBase['urlPattern'];
    allowedWebsites: AllowedWebsiteCustomization[];
};

export type IpwConfigDealertrackCustomization = IpwConfigBaseCustomization & {
    ipw_system_id: typeof IpwSystemIdKey.DEALERTRACK;
    reconciliation?: DealertrackReconciliationCustomization;
};

export type IpwConfigPcpartpickerCustomization = IpwConfigBaseCustomization & {
    ipw_system_id: typeof IpwSystemIdKey.PCPARTPICKER;
};

export type IpwConfigCustomization =
    | IpwConfigDealertrackCustomization
    | IpwConfigPcpartpickerCustomization;

export const IpwConfigCustomizationBaseSchema: z.ZodObject<{
    ipw_system_id: z.ZodType<IpwSystemIdKey>;
    urlPattern: typeof UrlPatternStringSchema;
    allowedWebsites: z.ZodArray<typeof AllowedWebsiteCustomizationSchema>;
}> = z.object({
    ipw_system_id: z.enum(IpwSystemIdKeyList),
    urlPattern: UrlPatternStringSchema,
    allowedWebsites: z.array(AllowedWebsiteCustomizationSchema),
});

export const IpwConfigDealertrackCustomizationSchema: z.ZodType<IpwConfigDealertrackCustomization> =
    IpwConfigCustomizationBaseSchema.extend({
        ipw_system_id: z.literal(IpwSystemIdKey.DEALERTRACK),
        reconciliation: DealertrackReconciliationCustomizationSchema.optional(),
    }).strict();

export const IpwConfigPcpartpickerCustomizationSchema: z.ZodType<IpwConfigPcpartpickerCustomization> =
    IpwConfigCustomizationBaseSchema.extend({
        ipw_system_id: z.literal(IpwSystemIdKey.PCPARTPICKER),
    }).strict();

export const IpwConfigCustomizationSchema: z.ZodType<IpwConfigCustomization> =
    z.union([
        IpwConfigDealertrackCustomizationSchema,
        IpwConfigPcpartpickerCustomizationSchema,
    ]);

export type IpgConfigBaseCustomization = {
    ipg_id: BaseIpgConfig['ipg_id'];
    urlPattern: BaseIpgConfig['urlPattern'];
    allowedWebsites: AllowedWebsiteCustomization[];
};

export type ValorIpgConfigCustomization = IpgConfigBaseCustomization & {
    ipg_id: typeof IpgSystemId.VALOR_US;
    default_api_url?: ValorIpgConfig['default_api_url'];
    default_channel?: ValorIpgConfig['default_channel'];
};

export type IpgConfigCustomization = ValorIpgConfigCustomization;

export const ValorIpgConfigCustomizationSchema: z.ZodType<ValorIpgConfigCustomization> =
    z
        .object({
            ipg_id: z.literal(IpgSystemId.VALOR_US),
            urlPattern: UrlPatternStringSchema,
            allowedWebsites: z.array(AllowedWebsiteCustomizationSchema),
            default_api_url: ValorIpgConfigSchema.shape.default_api_url,
            default_channel: ValorIpgConfigSchema.shape.default_channel,
        })
        .strict();

export const IpgConfigCustomizationSchema: z.ZodType<IpgConfigCustomization> =
    ValorIpgConfigCustomizationSchema;

export type IpwConfigCustomizationMap = Partial<
    Record<IpwSystemIdKey, IpwConfigCustomization>
>;

export const IpwConfigCustomizationMapSchema: z.ZodType<IpwConfigCustomizationMap> =
    z.partialRecord(IpwSystemIdSchema, IpwConfigCustomizationSchema);

export type IpgConfigCustomizationMap = Partial<
    Record<IpgSystemIdKey, IpgConfigCustomization>
>;

export const IpgConfigCustomizationMapSchema: z.ZodType<IpgConfigCustomizationMap> =
    z.partialRecord(IpgSystemIdSchema, IpgConfigCustomizationSchema);

export type UpdateIpwConfigCustomizationMap = Partial<
    Record<IpwSystemIdKey, IpwConfigCustomization | null>
>;

export const UpdateIpwConfigCustomizationMapSchema: z.ZodType<UpdateIpwConfigCustomizationMap> =
    z.partialRecord(
        IpwSystemIdSchema,
        IpwConfigCustomizationSchema.nullable()
    );

export type UpdateIpgConfigCustomizationMap = Partial<
    Record<IpgSystemIdKey, IpgConfigCustomization | null>
>;

export const UpdateIpgConfigCustomizationMapSchema: z.ZodType<UpdateIpgConfigCustomizationMap> =
    z.partialRecord(
        IpgSystemIdSchema,
        IpgConfigCustomizationSchema.nullable()
    );

export const USStateKeyList = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC',
] as const;
export const USStateSchema = z.enum(USStateKeyList);
export type USState = z.infer<typeof USStateSchema>;

export const CanadaProvinceKeyList = [
  'AB', // Alberta
  'BC', // British Columbia
  'MB', // Manitoba
  'NB', // New Brunswick
  'NL', // Newfoundland and Labrador
  'NS', // Nova Scotia
  'NT', // Northwest Territories
  'NU', // Nunavut
  'ON', // Ontario
  'PE', // Prince Edward Island
  'QC', // Quebec
  'SK', // Saskatchewan
  'YT', // Yukon
];
export const CanadaProvinceSchema = z.enum(CanadaProvinceKeyList);
export type CanadaProvince = z.infer<typeof CanadaProvinceSchema>;


export const USZipCode5Schema = z.string().regex(/^\d{5}$/);
export const USZipCode5Plus4Schema = z.string().regex(/^\d{5}-\d{4}$/);
export const USZipCodeSchema = z.union([USZipCode5Schema, USZipCode5Plus4Schema]);
export type USZipCode = z.infer<typeof USZipCodeSchema>;

export const normalizePhoneNumber = (value: string): string => value.replace(/[^0-9]/g, '');

export const formatPhoneNumber = (value: string): string => {
    const normalizedDigits: string = normalizePhoneNumber(value);
    const digits: string =
        normalizedDigits.length >= 11 && normalizedDigits.startsWith('1')
            ? normalizedDigits.slice(1, 11)
            : normalizedDigits.slice(0, 10);
    if (digits.length === 0) {
        return '';
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeNorthAmericanPhoneNumberToE164 = (value: string): string => {
    const trimmedValue: string = value.trim();
    const normalizedDigits: string = normalizePhoneNumber(trimmedValue);

    if (trimmedValue.length === 0) {
        return '';
    }

    if (trimmedValue.startsWith('+')) {
        return normalizedDigits.length === 0 ? '' : `+${normalizedDigits}`;
    }

    if (normalizedDigits.length === 10) {
        return `+1${normalizedDigits}`;
    }

    if (normalizedDigits.length === 11 && normalizedDigits.startsWith('1')) {
        return `+${normalizedDigits}`;
    }

    return trimmedValue;
};

export const isNormalizedPhoneNumberValid = (value: string): boolean =>
    normalizePhoneNumber(value).length === 10;

export const getPhoneNumberValidationMessage = (value: string): string | null => {
    const trimmedValue: string = value.trim();
    if (trimmedValue.length === 0) {
        return null;
    }
    if (!isNormalizedPhoneNumberValid(trimmedValue)) {
        return 'Phone number must be 10 digits.';
    }
    return null;
};

export const normalizeUSZipCode = (value: string): string => {
    const digits: string = value.replace(/[^0-9]/g, '').slice(0, 9);
    if (digits.length <= 5) {
        return digits;
    }
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const CanadaPostalCodeSchema = z.string().regex(
  /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
  "Invalid Canadian Postal Code (Expected Format: A1A 1A1)"
);
export type CanadaPostalCode = z.infer<typeof CanadaPostalCodeSchema>;

export const normalizeCanadaPostalCode = (value: string): string => {
    const normalizedChars: string[] = [];
    const upperValue: string = value.toUpperCase();
    for (const char of upperValue) {
        if (normalizedChars.length >= 6) {
            break;
        }
        const position: number = normalizedChars.length;
        const expectsLetter: boolean = position % 2 === 0;
        if (expectsLetter) {
            if (!/[A-Z]/.test(char)) {
                continue;
            }
            normalizedChars.push(char);
            continue;
        }
        if (!/[0-9]/.test(char)) {
            continue;
        }
        normalizedChars.push(char);
    }
    const compact: string = normalizedChars.join('');
    if (compact.length <= 3) {
        return compact;
    }
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
};

export const AddressUSSchema = z.object({
    country: z.literal('US'),
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: USStateSchema,
    zip: USZipCodeSchema,
});

export type AddressUS = z.infer<typeof AddressUSSchema>;

export const AddressCanadaSchema = z.object({
    country: z.literal('CA'),
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    province: CanadaProvinceSchema,
    postal_code: CanadaPostalCodeSchema,
});

export type AddressCanada = z.infer<typeof AddressCanadaSchema>;

export type Address = AddressCanada | AddressUS;

export const AddressDiscriminatedUnionSchema: z.ZodType<Address> = z.discriminatedUnion('country', [
    AddressCanadaSchema,
    AddressUSSchema,
]);

export const AddressSchema: z.ZodType<Address> = AddressDiscriminatedUnionSchema;

export type PartialAddressUS = Partial<AddressUS> & {
    country: 'US';
};

export const PartialAddressUSSchema: z.ZodType<PartialAddressUS> =
    AddressUSSchema.partial().extend({
        country: z.literal('US'),
    });

export type PartialAddressCanada = Partial<AddressCanada> & {
    country: 'CA';
};

export const PartialAddressCanadaSchema: z.ZodType<PartialAddressCanada> =
    AddressCanadaSchema.partial().extend({
        country: z.literal('CA'),
    });

export type BusinessDetailsCore = {
    legalEntityName: string;
    dba?: string;
    department?: string;
    main_phone_number?: string;
}

export const BusinessDetailsCoreSchema = z.object({
    legalEntityName: z.string(),
    dba: z.string().optional(),
    department: z.string().optional(),
    main_phone_number: z.string().optional(),
})

export type BusinessDetailsUS = BusinessDetailsCore & {
    country: 'US';
    defaultCurrency: 'USD',
    address?: PartialAddressUS;
};

export const BusinessDetailsUSSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('US'),
    defaultCurrency: z.literal('USD').default('USD'),
    address: PartialAddressUSSchema.optional(),
});

export type BusinessDetailsCanada = BusinessDetailsCore & {
    country: 'CA';
    defaultCurrency: 'CAD',
    address?: PartialAddressCanada;
};

export const BusinessDetailsCanadaSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('CA'),
    defaultCurrency: z.literal('CAD').default('CAD'),
    address: PartialAddressCanadaSchema.optional(),
});

export type BusinessDetails = BusinessDetailsUS | BusinessDetailsCanada;

export const BusinessDetailsSchema = z.discriminatedUnion('country', [
    BusinessDetailsUSSchema,
    BusinessDetailsCanadaSchema,
]);

export type PlatformPaymentMethods = {
    terminal: boolean;
    card: boolean;
    ach: boolean;
    e_invoice: boolean;
}

export const PlatformPaymentMethodsSchema: z.ZodType<PlatformPaymentMethods> = z.object({
    terminal: z.boolean().default(false),
    card: z.boolean().default(false),
    ach: z.boolean().default(false),
    e_invoice: z.boolean().default(false),
});

export type PlatformFeatures = {
    payment_methods: PlatformPaymentMethods;
    customerPortal: CustomerPortalConfig | null;
}

export const PlatformFeaturesSchema: z.ZodType<PlatformFeatures> = z.object({
    payment_methods: PlatformPaymentMethodsSchema.default({
        terminal: false,
        card: false,
        ach: false,
        e_invoice: false,
    }),
    customerPortal: CustomerPortalConfigSchema.nullable().default(null),
});

export type IpsConfig = {
    automaticCustomerDetailsDetectionEnabled: boolean;
    customerDetailsDetectionButtonEnabled: boolean;
    addressRequiredForCardPayment: boolean;
}

export const ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled: boolean = false;
export const ResolvedDefaultCustomerDetailsDetectionButtonEnabled: boolean = false;
export const ResolvedDefaultAddressRequiredForCardPayment: boolean = false;

export const ResolvedDefaultIpsConfig: IpsConfig = {
    automaticCustomerDetailsDetectionEnabled:
        ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled,
    customerDetailsDetectionButtonEnabled:
        ResolvedDefaultCustomerDetailsDetectionButtonEnabled,
    addressRequiredForCardPayment:
        ResolvedDefaultAddressRequiredForCardPayment,
};

export const IpsConfigSchema: z.ZodObject<{
    automaticCustomerDetailsDetectionEnabled: z.ZodDefault<z.ZodBoolean>;
    customerDetailsDetectionButtonEnabled: z.ZodDefault<z.ZodBoolean>;
    addressRequiredForCardPayment: z.ZodDefault<z.ZodBoolean>;
}> = z.object({
    automaticCustomerDetailsDetectionEnabled: z.boolean().default(
        ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled
    ),
    customerDetailsDetectionButtonEnabled: z.boolean().default(
        ResolvedDefaultCustomerDetailsDetectionButtonEnabled
    ),
    addressRequiredForCardPayment: z.boolean().default(
        ResolvedDefaultAddressRequiredForCardPayment
    ),
});

export type PlatformConfig = {
    features: PlatformFeatures;
    payment_request_email_cc: string[];
    require2FA: boolean;
}

export const PlatformConfigSchema: z.ZodType<PlatformConfig> = z.object({
    features: PlatformFeaturesSchema,
    payment_request_email_cc: z.array(z.string().email()).default([]),
    require2FA: z.boolean().default(false),
});


export type ProcessorConfig = {
    reporting_dashboard?: IpgAllowedWebsite,
}

export const ProcessorConfigSchema: z.ZodType<ProcessorConfig> = z.object({
    reporting_dashboard: AllowedWebsiteSchemaIPG.optional()
})

export const ProcessorConfigStorageSchema: z.ZodType<ProcessorConfig> =
    z.preprocess(normalizeLegacyProcessorConfigSystemIds, ProcessorConfigSchema);

export type MerchantUserRoleAssignmentConfig = {
    organizationKind: 'MERCHANT',
    defaultPhysicalTerminalDevice?: string, // UUID
    defaultVirtualTerminalDevice?: string, // UUID
    lastPhysicalTerminalDevice?: string, // UUID
    lastVirtualTerminalDevice?: string, // UUID
}
export type IsoUserRoleAssignmentConfig = {
    organizationKind: 'ISO'
}
export type CustomerUserRoleAssignmentConfig = {
    organizationKind: 'CUSTOMER'
}
export type UserRoleAssignmentConfig = MerchantUserRoleAssignmentConfig | IsoUserRoleAssignmentConfig | CustomerUserRoleAssignmentConfig;

export type MerchantUserRoleAssignmentConfigPatch = {
    organizationKind?: 'MERCHANT';
    defaultPhysicalTerminalDevice?: string;
    defaultVirtualTerminalDevice?: string;
    lastPhysicalTerminalDevice?: string;
    lastVirtualTerminalDevice?: string;
};
export type IsoUserRoleAssignmentConfigPatch = {
    organizationKind?: 'ISO';
};
export type CustomerUserRoleAssignmentConfigPatch = {
    organizationKind?: 'CUSTOMER';
};
export type UserRoleAssignmentConfigPatch =
    | MerchantUserRoleAssignmentConfigPatch
    | IsoUserRoleAssignmentConfigPatch
    | CustomerUserRoleAssignmentConfigPatch;

export const MerchantUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'MERCHANT'>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z.object({
    organizationKind: z.literal('MERCHANT'),
    defaultPhysicalTerminalDevice: z.string().uuid().optional(),
    defaultVirtualTerminalDevice: z.string().uuid().optional(),
    lastPhysicalTerminalDevice: z.string().uuid().optional(),
    lastVirtualTerminalDevice: z.string().uuid().optional(),
});
export const IsoUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'ISO'>;
}> = z.object({
    organizationKind: z.literal('ISO'),
});
export const CustomerUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'CUSTOMER'>;
}> = z.object({
    organizationKind: z.literal('CUSTOMER'),
});
export const UserRoleAssignmentConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof MerchantUserRoleAssignmentConfigSchema,
        typeof IsoUserRoleAssignmentConfigSchema,
        typeof CustomerUserRoleAssignmentConfigSchema,
    ],
    'organizationKind'
> = z.discriminatedUnion('organizationKind', [
    MerchantUserRoleAssignmentConfigSchema,
    IsoUserRoleAssignmentConfigSchema,
    CustomerUserRoleAssignmentConfigSchema,
]);

export const MerchantUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'MERCHANT'>>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        organizationKind: z.literal('MERCHANT').optional(),
        defaultPhysicalTerminalDevice: z.string().uuid().optional(),
        defaultVirtualTerminalDevice: z.string().uuid().optional(),
        lastPhysicalTerminalDevice: z.string().uuid().optional(),
        lastVirtualTerminalDevice: z.string().uuid().optional(),
    })
    .strict();

export const IsoUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'ISO'>>;
}> = z
    .object({
        organizationKind: z.literal('ISO').optional(),
    })
    .strict();

export const CustomerUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'CUSTOMER'>>;
}> = z
    .object({
        organizationKind: z.literal('CUSTOMER').optional(),
    })
    .strict();

export const UserRoleAssignmentConfigPatchSchema: z.ZodUnion<
    [
        typeof MerchantUserRoleAssignmentConfigPatchSchema,
        typeof IsoUserRoleAssignmentConfigPatchSchema,
        typeof CustomerUserRoleAssignmentConfigPatchSchema,
    ]
> = z.union([
    MerchantUserRoleAssignmentConfigPatchSchema,
    IsoUserRoleAssignmentConfigPatchSchema,
    CustomerUserRoleAssignmentConfigPatchSchema,
]);

export type MerchantConfig = {
    requires_password_change: boolean;
    businessDetails: BusinessDetails | null;
    ipg: Partial<Record<IpgSystemIdKey, IpgConfig>>;
    ipw: Partial<Record<IpwSystemIdKey, IpwConfig>>;
    ips: IpsConfig;
    processorConfig: ProcessorConfig[];
    sambaConfig: SambaConfig;
    platformConfig: PlatformConfig;
    userRoleAssignmentConfig: UserRoleAssignmentConfig | null;
}

type IsTuple<T extends readonly unknown[]> = number extends T['length'] ? false : true;

export type DeepPartial<T> =
    T extends (...args: never[]) => unknown
        ? T
        : T extends readonly unknown[]
            ? IsTuple<T> extends true
                ? { [K in keyof T]?: DeepPartial<T[K]> }
                : T extends unknown[]
                    ? Array<DeepPartial<T[number]>>
                    : ReadonlyArray<DeepPartial<T[number]>>
            : T extends object
                ? { [K in keyof T]?: DeepPartial<T[K]> }
                : T | undefined;

export type MerchantConfigStorage = DeepPartial<
    Omit<MerchantConfig, 'sambaConfig'>
> & {
    sambaConfig?: StoredSambaConfig;
};

export type MerchantConfigInput = DeepPartial<MerchantConfig>;

type DeepOptionalObjectShape = Record<string, z.ZodTypeAny>;
type DeepOptionalUnionOptions = ReadonlyArray<z.ZodTypeAny>;
type DeepOptionalRecordKeySchema = z.ZodType<string | number | symbol>;

function isZodDefaultSchema(schema: z.ZodTypeAny): schema is z.ZodDefault<z.ZodTypeAny> {
    return schema instanceof z.ZodDefault;
}

function isZodOptionalSchema(schema: z.ZodTypeAny): schema is z.ZodOptional<z.ZodTypeAny> {
    return schema instanceof z.ZodOptional;
}

function isZodNullableSchema(schema: z.ZodTypeAny): schema is z.ZodNullable<z.ZodTypeAny> {
    return schema instanceof z.ZodNullable;
}

function isZodReadonlySchema(schema: z.ZodTypeAny): schema is z.ZodReadonly<z.ZodTypeAny> {
    return schema instanceof z.ZodReadonly;
}

function isZodObjectSchema(schema: z.ZodTypeAny): schema is z.ZodObject<DeepOptionalObjectShape> {
    return schema instanceof z.ZodObject;
}

function isZodArraySchema(schema: z.ZodTypeAny): schema is z.ZodArray<z.ZodTypeAny> {
    return schema instanceof z.ZodArray;
}

function isZodRecordSchema(
    schema: z.ZodTypeAny
): schema is z.ZodRecord<DeepOptionalRecordKeySchema, z.ZodTypeAny> {
    return schema instanceof z.ZodRecord;
}

function isZodUnionSchema(schema: z.ZodTypeAny): schema is z.ZodUnion<DeepOptionalUnionOptions> {
    return schema instanceof z.ZodUnion;
}

function isZodDiscriminatedUnionSchema(
    schema: z.ZodTypeAny
): schema is z.ZodDiscriminatedUnion<DeepOptionalUnionOptions, string> {
    return schema instanceof z.ZodDiscriminatedUnion;
}

function removeSchemaDefaults(schema: z.ZodTypeAny): z.ZodTypeAny {
    if (isZodDefaultSchema(schema)) {
        return removeSchemaDefaults(schema.removeDefault());
    }
    return schema;
}

function createUnionFromOptions(options: ReadonlyArray<z.ZodTypeAny>): z.ZodTypeAny {
    const [firstOption, secondOption, ...remainingOptions] = options;

    if (firstOption === undefined) {
        return z.never();
    }
    if (secondOption === undefined) {
        return firstOption;
    }
    return z.union([firstOption, secondOption, ...remainingOptions]);
}

function createDeepOptionalSchemaInternal(schema: z.ZodTypeAny): z.ZodTypeAny {
    const unwrappedSchema: z.ZodTypeAny = removeSchemaDefaults(schema);

    if (isZodOptionalSchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(unwrappedSchema.unwrap()).optional();
    }

    if (isZodNullableSchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(unwrappedSchema.unwrap()).nullable().optional();
    }

    if (isZodReadonlySchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(unwrappedSchema.unwrap()).readonly().optional();
    }

    if (isZodObjectSchema(unwrappedSchema)) {
        const nextShape: Record<string, z.ZodTypeAny> = {};
        const currentShape: Readonly<DeepOptionalObjectShape> = unwrappedSchema.shape;

        for (const shapeKey in currentShape) {
            const shapeValue: z.ZodTypeAny | undefined = currentShape[shapeKey];
            if (shapeValue === undefined) {
                continue;
            }
            nextShape[shapeKey] = createDeepOptionalSchemaInternal(shapeValue);
        }
        return z.object(nextShape).partial();
    }

    if (isZodArraySchema(unwrappedSchema)) {
        return z.array(createDeepOptionalSchemaInternal(unwrappedSchema.element)).optional();
    }

    if (isZodRecordSchema(unwrappedSchema)) {
        return z
            .record(
                unwrappedSchema.keyType,
                createDeepOptionalSchemaInternal(unwrappedSchema.valueType)
            )
            .optional();
    }

    if (isZodUnionSchema(unwrappedSchema)) {
        return createUnionFromOptions(
            unwrappedSchema.options.map((option) => createDeepOptionalSchemaInternal(option))
        ).optional();
    }

    if (isZodDiscriminatedUnionSchema(unwrappedSchema)) {
        return createUnionFromOptions(
            Array.from(unwrappedSchema.options).map((option) => createDeepOptionalSchemaInternal(option))
        ).optional();
    }

    return unwrappedSchema.optional();
}

function createDeepOptionalSchema<T>(schema: z.ZodType<T>): z.ZodType<DeepPartial<T>>;
function createDeepOptionalSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
    return createDeepOptionalSchemaInternal(schema);
}

export const MerchantConfigObjectSchema: z.ZodType<MerchantConfig> = z.object({
    requires_password_change: z.boolean().default(false),
    businessDetails: BusinessDetailsSchema.nullable(),
    ipg: z.partialRecord(IpgSystemIdSchema, IpgConfigSchema),
    ipw: z.partialRecord(IpwSystemIdSchema, IpwConfigSchema),
    ips: IpsConfigSchema,
    processorConfig: z.array(ProcessorConfigSchema),
    sambaConfig: SambaConfigSchema,
    platformConfig: PlatformConfigSchema,
    userRoleAssignmentConfig: UserRoleAssignmentConfigSchema.nullable(),
});

function createResolvedDefaultMerchantConfig(): MerchantConfig {
    return {
        requires_password_change: false,
        businessDetails: null,
        ipg: {},
        ipw: {},
        ips: {
            ...ResolvedDefaultIpsConfig,
        },
        processorConfig: [],
        sambaConfig: {
            allowPromoteSessionCookie: false,
            unmanagedAllowedWebsites: [],
            credentialMetadata: [],
        },
        platformConfig: {
            features: {
                payment_methods: {
                    terminal: false,
                    card: false,
                    ach: false,
                    e_invoice: false,
                },
                customerPortal: null,
            },
            payment_request_email_cc: [],
            require2FA: false,
        },
        userRoleAssignmentConfig: null,
    };
}

type ResolvedMerchantConfigPrimitive = boolean | number | string | null | undefined;

type ResolvedMerchantConfigObject = object;

type ResolvedMerchantConfigValue =
    | ResolvedMerchantConfigPrimitive
    | ResolvedMerchantConfigObject
    | ReadonlyArray<ResolvedMerchantConfigValue>;

interface ResolvedMerchantConfigRecord {
    [key: string]: ResolvedMerchantConfigValue;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return Object.prototype.toString.call(value) === '[object Object]';
}

function createThirdPartySystemIdByKeyMap<SystemKey extends string>(
    systemKeys: readonly SystemKey[],
    systemIdsByKey: Readonly<Record<SystemKey, ThirdPartySystemId>>
): ReadonlyMap<string, ThirdPartySystemId> {
    const systemIdByKey = new Map<string, ThirdPartySystemId>();
    for (const systemKey of systemKeys) {
        systemIdByKey.set(systemKey, systemIdsByKey[systemKey]);
    }
    return systemIdByKey;
}

const IpgSystemIdByKey: ReadonlyMap<string, ThirdPartySystemId> =
    createThirdPartySystemIdByKeyMap(IpgSystemIdKeyList, IpgSystemId);

const IpwSystemIdByKey: ReadonlyMap<string, ThirdPartySystemId> =
    createThirdPartySystemIdByKeyMap(IpwSystemIdKeyList, IpwSystemId);

function normalizeLegacyAllowedWebsiteSystemId(
    value: unknown,
    systemId: ThirdPartySystemId
): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    if ('system_id' in value) {
        return value;
    }
    return {
        ...value,
        system_id: systemId,
    };
}

function normalizeLegacyAllowedWebsiteArraySystemIds(
    value: unknown,
    systemId: ThirdPartySystemId
): unknown {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map((entry) =>
        normalizeLegacyAllowedWebsiteSystemId(entry, systemId)
    );
}

function normalizeLegacyIpxConfigSystemIds(
    value: unknown,
    systemIdByKey: ReadonlyMap<string, ThirdPartySystemId>
): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    const normalizedConfigMap: Record<string, unknown> = {};
    for (const systemKey in value) {
        const systemConfig: unknown = value[systemKey];
        const systemId: ThirdPartySystemId | undefined =
            systemIdByKey.get(systemKey);
        if (systemId === undefined || !isPlainRecord(systemConfig)) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        normalizedConfigMap[systemKey] = {
            ...systemConfig,
            allowedWebsites: normalizeLegacyAllowedWebsiteArraySystemIds(
                systemConfig.allowedWebsites,
                systemId
            ),
        };
    }
    return normalizedConfigMap;
}

function stripLegacyStoredSambaConfigUnmanagedAllowedWebsites(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    const { unmanagedAllowedWebsites: _unmanagedAllowedWebsites, ...storedSambaConfig } =
        value;
    return storedSambaConfig;
}

function legacyProcessorReportingDashboardSystemId(): ThirdPartySystemId {
    const firstIpgSystemKey = IpgSystemIdKeyList[0];
    if (firstIpgSystemKey === undefined) {
        throw new Error('No IPG system ids are available for legacy processor config.');
    }
    const systemId = IpgSystemIdByKey.get(firstIpgSystemKey);
    if (systemId === undefined) {
        throw new Error(
            `Missing IPG system id mapping for legacy processor config: ${firstIpgSystemKey}`
        );
    }
    return systemId;
}

function normalizeLegacyProcessorConfigSystemIds(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    if (!('reporting_dashboard' in value)) {
        return value;
    }
    return {
        ...value,
        reporting_dashboard: normalizeLegacyAllowedWebsiteSystemId(
            value.reporting_dashboard,
            legacyProcessorReportingDashboardSystemId()
        ),
    };
}

function normalizeLegacyProcessorConfigListSystemIds(value: unknown): unknown {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map((processorConfig) =>
        normalizeLegacyProcessorConfigSystemIds(processorConfig)
    );
}

function normalizeLegacyMerchantConfigStorage(config: unknown): unknown {
    if (!isPlainRecord(config)) {
        return config;
    }

    const normalizedConfig: Record<string, unknown> = { ...config };
    if ('ipg' in config) {
        normalizedConfig.ipg = normalizeLegacyIpxConfigSystemIds(
            config.ipg,
            IpgSystemIdByKey
        );
    }
    if ('ipw' in config) {
        normalizedConfig.ipw = normalizeLegacyIpxConfigSystemIds(
            config.ipw,
            IpwSystemIdByKey
        );
    }
    if ('sambaConfig' in config) {
        normalizedConfig.sambaConfig =
            stripLegacyStoredSambaConfigUnmanagedAllowedWebsites(config.sambaConfig);
    }
    if ('processorConfig' in config) {
        normalizedConfig.processorConfig = normalizeLegacyProcessorConfigListSystemIds(
            config.processorConfig
        );
    }
    return normalizedConfig;
}

function normalizeLegacyMerchantConfigRuntime(config: unknown): unknown {
    if (!isPlainRecord(config)) {
        return config;
    }

    const normalizedConfig: Record<string, unknown> = { ...config };
    if ('ipg' in config) {
        normalizedConfig.ipg = normalizeLegacyIpxConfigSystemIds(
            config.ipg,
            IpgSystemIdByKey
        );
    }
    if ('ipw' in config) {
        normalizedConfig.ipw = normalizeLegacyIpxConfigSystemIds(
            config.ipw,
            IpwSystemIdByKey
        );
    }
    if ('processorConfig' in config) {
        normalizedConfig.processorConfig = normalizeLegacyProcessorConfigListSystemIds(
            config.processorConfig
        );
    }
    return normalizedConfig;
}

function validateIpxAllowedWebsiteSystemIds(
    ctx: z.RefinementCtx,
    pathPrefix: 'ipg' | 'ipw',
    configMap: Partial<Record<string, { allowedWebsites: AllowedWebsite[] }>>,
    systemIdByKey: ReadonlyMap<string, ThirdPartySystemId>
): void {
    for (const systemKey in configMap) {
        const systemConfig = configMap[systemKey];
        const expectedSystemId = systemIdByKey.get(systemKey);
        if (systemConfig === undefined || expectedSystemId === undefined) {
            continue;
        }
        for (const [websiteIndex, allowedWebsite] of
            systemConfig.allowedWebsites.entries()) {
            if (allowedWebsite.system_id === expectedSystemId) {
                continue;
            }
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                    pathPrefix,
                    systemKey,
                    'allowedWebsites',
                    websiteIndex,
                    'system_id',
                ],
                message: `Allowed website system_id must match ${systemKey}.`,
            });
        }
    }
}

function validateMerchantConfigNestedAllowedWebsiteSystemIds(
    config: MerchantConfig,
    ctx: z.RefinementCtx
): void {
    validateIpxAllowedWebsiteSystemIds(ctx, 'ipg', config.ipg, IpgSystemIdByKey);
    validateIpxAllowedWebsiteSystemIds(ctx, 'ipw', config.ipw, IpwSystemIdByKey);
}

function isResolvedMerchantConfigObject(
    value: ResolvedMerchantConfigValue
): value is ResolvedMerchantConfigObject {
    return (
        value !== null &&
        value !== undefined &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function toResolvedMerchantConfigRecord(
    value: ResolvedMerchantConfigObject
): ResolvedMerchantConfigRecord {
    // Safe bridge: these object branches come from MerchantConfig or
    // MerchantConfigStorage values validated by Zod within this module.
    // eslint-disable-next-line no-restricted-syntax
    return value as ResolvedMerchantConfigRecord;
}

function deepMergeResolvedMerchantConfigValue(
    baseValue: ResolvedMerchantConfigValue,
    overrideValue: ResolvedMerchantConfigValue
): ResolvedMerchantConfigValue {
    if (overrideValue === undefined) {
        return baseValue;
    }

    if (Array.isArray(overrideValue)) {
        return overrideValue;
    }

    if (
        isResolvedMerchantConfigObject(baseValue) &&
        isResolvedMerchantConfigObject(overrideValue)
    ) {
        const baseRecord: ResolvedMerchantConfigRecord =
            toResolvedMerchantConfigRecord(baseValue);
        const overrideRecord: ResolvedMerchantConfigRecord =
            toResolvedMerchantConfigRecord(overrideValue);
        const mergedRecord: ResolvedMerchantConfigRecord = {
            ...baseRecord,
        };
        for (const overrideKey in overrideRecord) {
            const overrideEntryValue = overrideRecord[overrideKey];
            const baseEntryValue = baseRecord[overrideKey];
            const mergedEntryValue = deepMergeResolvedMerchantConfigValue(
                baseEntryValue,
                overrideEntryValue
            );
            if (mergedEntryValue !== undefined) {
                mergedRecord[overrideKey] = mergedEntryValue;
            }
        }
        return mergedRecord;
    }

    return overrideValue;
}

function mergeResolvedMerchantConfigStorageValue(
    baseConfig: MerchantConfig,
    storageConfig: MerchantConfigStorage | undefined
): ResolvedMerchantConfigValue {
    return deepMergeResolvedMerchantConfigValue(
        baseConfig,
        MerchantConfigStorageSchema.parse(storageConfig ?? {})
    );
}

function resolveMerchantConfigFromInput(
    config: MerchantConfigInput | undefined
): unknown {
    return deepMergeResolvedMerchantConfigValue(
        createResolvedDefaultMerchantConfig(),
        config ?? {}
    );
}

export function mergeMerchantConfigStorageIntoBaseConfig(
    baseConfig: MerchantConfig,
    storageConfig: MerchantConfigStorage | undefined
): MerchantConfig {
    return MerchantConfigResolvedSchema.parse(
        mergeResolvedMerchantConfigStorageValue(baseConfig, storageConfig)
    );
}

function normalizeResolvedConfigValueForStorage(
    resolvedValue: unknown,
    defaultValue: unknown
): unknown {
    if (resolvedValue === null || resolvedValue === undefined) {
        return undefined;
    }

    if (Array.isArray(resolvedValue)) {
        return resolvedValue.length === 0 &&
            Array.isArray(defaultValue) &&
            defaultValue.length === 0
            ? undefined
            : resolvedValue;
    }

    if (isPlainRecord(resolvedValue)) {
        const defaultRecord = isPlainRecord(defaultValue) ? defaultValue : {};
        const normalizedRecord: Record<string, unknown> = {};
        for (const resolvedKey in resolvedValue) {
            const normalizedEntryValue = normalizeResolvedConfigValueForStorage(
                resolvedValue[resolvedKey],
                defaultRecord[resolvedKey]
            );
            if (normalizedEntryValue !== undefined) {
                normalizedRecord[resolvedKey] = normalizedEntryValue;
            }
        }
        return Object.keys(normalizedRecord).length === 0 ? undefined : normalizedRecord;
    }

    return Object.is(resolvedValue, defaultValue) ? undefined : resolvedValue;
}

export const MerchantConfigStorageObjectSchema: z.ZodType<MerchantConfigStorage> =
    z.object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: createDeepOptionalSchema(
            z.partialRecord(IpgSystemIdSchema, IpgConfigSchema)
        ),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        processorConfig: createDeepOptionalSchema(z.array(ProcessorConfigSchema)),
        sambaConfig: StoredSambaConfigSchema.optional(),
        platformConfig: createDeepOptionalSchema(PlatformConfigSchema).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    });

export const MerchantConfigStorageSchema: z.ZodType<MerchantConfigStorage> =
    z.preprocess(
        normalizeLegacyMerchantConfigStorage,
        MerchantConfigStorageObjectSchema
    );

export const MerchantConfigStorageWriteObjectSchema: z.ZodType<MerchantConfigStorage> =
    z.object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: createDeepOptionalSchema(
            z.partialRecord(IpgSystemIdSchema, IpgConfigSchema)
        ),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        processorConfig: createDeepOptionalSchema(z.array(ProcessorConfigSchema)),
        sambaConfig: StoredSambaConfigWriteSchema.optional(),
        platformConfig: createDeepOptionalSchema(PlatformConfigSchema).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    });

export const MerchantConfigStorageWriteSchema: z.ZodType<MerchantConfigStorage> =
    z.preprocess(
        normalizeLegacyMerchantConfigRuntime,
        MerchantConfigStorageWriteObjectSchema
    );

export const MerchantConfigInputObjectSchema: z.ZodType<MerchantConfigInput> =
    createDeepOptionalSchema(MerchantConfigObjectSchema);

export const MerchantConfigResolvedSchema: z.ZodType<MerchantConfig> =
    MerchantConfigObjectSchema.superRefine(
        validateMerchantConfigNestedAllowedWebsiteSystemIds
    );

export const MerchantConfigSchema: z.ZodType<MerchantConfig> =
    z.preprocess(
        normalizeLegacyMerchantConfigRuntime,
        MerchantConfigInputObjectSchema
    ).transform((config) =>
        resolveMerchantConfigFromInput(config)
    ).pipe(MerchantConfigResolvedSchema);

export function normalizeMerchantConfigStorage(
    config: MerchantConfig
): MerchantConfigStorage {
    const normalizedConfigValue = normalizeResolvedConfigValueForStorage(
        config,
        createResolvedDefaultMerchantConfig()
    );

    return MerchantConfigStorageSchema.parse(
        isPlainRecord(normalizedConfigValue) ? normalizedConfigValue : {}
    );
}

export const resolveMerchantConfigIpsConfig = (
    merchantConfig?: MerchantConfig
): IpsConfig => MerchantConfigSchema.parse(merchantConfig ?? {}).ips;

export const resolveMerchantConfigAddressRequiredForCardPayment = (
    merchantConfig: MerchantConfig | undefined
): boolean => resolveMerchantConfigIpsConfig(merchantConfig).addressRequiredForCardPayment;

export const RoleAssignmentIpsConfigSchema: z.ZodType<DeepPartial<IpsConfig>> =
    createDeepOptionalSchema(IpsConfigSchema);

export type RoleAssignmentCustomConfig = DeepPartial<MerchantConfig>;

export const RoleAssignmentCustomConfigObjectSchema: z.ZodType<RoleAssignmentCustomConfig> =
    createDeepOptionalSchema(MerchantConfigObjectSchema);

export const RoleAssignmentCustomConfigSchema: z.ZodType<RoleAssignmentCustomConfig> =
    RoleAssignmentCustomConfigObjectSchema;

function allowedWebsiteKey(allowedWebsite: AllowedWebsite): string {
    return [
        allowedWebsite.type,
        String(allowedWebsite.system_id),
        allowedWebsite.template_name ?? '',
        allowedWebsite.name,
        allowedWebsite.url,
        allowedWebsite.sessionPersistence,
        allowedWebsite.promoteSessionCookies ? 'promote' : 'do-not-promote',
        allowedWebsite.allowedUrlPatterns.join('\u0000'),
        allowedWebsite.fetchDefaultAction,
        JSON.stringify(allowedWebsite.urlPatternsRules),
        allowedWebsite.iframeDefaultAction,
        JSON.stringify(allowedWebsite.iframeUrlPatterns),
    ].join('\u0001');
}

export function allowedWebsitesOfMerchantConfig(merchantConfig: MerchantConfig): AllowedWebsite[] {
    const combinedAllowedWebsites: AllowedWebsite[] = [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.allowedWebsites),
        ...Object.values(merchantConfig.ipw).flatMap((ipw) => ipw.allowedWebsites),
        ...(merchantConfig.sambaConfig?.unmanagedAllowedWebsites ?? []),
        ...merchantConfig.processorConfig.flatMap(
            (processorConfig): AllowedWebsite[] =>
                processorConfig.reporting_dashboard === undefined
                    ? []
                    : [processorConfig.reporting_dashboard]
        ),
    ];
    const dedupedAllowedWebsites = new Map<string, AllowedWebsite>();

    for (const allowedWebsite of combinedAllowedWebsites) {
        dedupedAllowedWebsites.set(allowedWebsiteKey(allowedWebsite), allowedWebsite);
    }

    return Array.from(dedupedAllowedWebsites.values());
}

export function rpaModulesOfMerchantConfig(merchantConfig: MerchantConfig): RpaModuleName[] {
    return [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.rpaModules),
        ...Object.values(merchantConfig.ipw).flatMap((ipw) => ipw.rpaModules),
    ]
}
